import { useMemo } from "react";
import moment from "moment/moment";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import { capitalizeFirstLetter } from "../../api/Functions";
import {
  inputValueToPeriode,
  periodeLabel,
  periodeToInputValue,
  type Periode,
} from "./comptaUtils";

const YEARS_BACK = 6;

interface PickerOption {
  value: string;
  label: string;
}

interface MoisPickerProps {
  label: string;
  value: Periode;
  onChange: (periode: Periode) => void;
  className?: string;
}

function buildMoisOptions(): PickerOption[] {
  const options: PickerOption[] = [];
  const cursor = moment().startOf("month");
  const min = moment().subtract(YEARS_BACK, "years").startOf("year");
  while (cursor.isSameOrAfter(min)) {
    options.push({
      value: cursor.format("YYYY-MM"),
      label: capitalizeFirstLetter(cursor.format("MMMM YYYY")),
    });
    cursor.subtract(1, "month");
  }
  return options;
}

export default function MoisPicker({
  label,
  value,
  onChange,
  className,
}: MoisPickerProps) {
  const options = useMemo(() => buildMoisOptions(), []);
  const current = periodeToInputValue(value);
  const selected = options.find((option) => option.value === current) ?? {
    value: current,
    label: capitalizeFirstLetter(periodeLabel(value)),
  };

  return (
    <ReactSelectStandalone
      label={label}
      className={className}
      options={options}
      value={selected}
      onChange={(option: PickerOption) => {
        const periode = inputValueToPeriode(option.value);
        if (periode) onChange(periode);
      }}
    />
  );
}

interface AnneePickerProps {
  label: string;
  value: number;
  onChange: (year: number) => void;
  className?: string;
}

export function AnneePicker({
  label,
  value,
  onChange,
  className,
}: AnneePickerProps) {
  const options = useMemo<PickerOption[]>(() => {
    const currentYear = moment().year();
    return Array.from({ length: YEARS_BACK + 1 }, (_, index) => ({
      value: String(currentYear - index),
      label: String(currentYear - index),
    }));
  }, []);

  return (
    <ReactSelectStandalone
      label={label}
      className={className}
      options={options}
      value={{ value: String(value), label: String(value) }}
      onChange={(option: PickerOption) => onChange(parseInt(option.value))}
    />
  );
}
